/**
 * Catalog of every field the conversation advanced-filter panel can render.
 *
 * The panel shows a per-user subset of these (persisted on
 * `users.conversation_filter_fields` via GET/PUT /agent/me/filter-fields) so an
 * agent only sees the filters they actually use. `DEFAULT_FILTER_FIELDS` is
 * what a user who has never customised the form gets.
 */

export type FilterFieldKey =
  | 'keyword'
  | 'referenceId'
  | 'subject'
  | 'description'
  | 'clientId'
  | 'locationId'
  | 'branchId'
  | 'departmentId'
  | 'campaignId'
  | 'teamId'
  | 'agents'
  | 'dateRange'
  | 'channel'
  | 'typeId'
  | 'conversationStatusId'
  | 'status'
  | 'stateId'
  | 'categoryId'
  | 'subcategoryId'
  | 'impactId'
  | 'urgencyId'
  | 'priorityId'
  | 'complexityId'
  | 'approvalStateId'
  | 'approvalLevelId'
  | 'dynamicProperty'

/** Group headings, in the order the panel renders them. */
export const FILTER_GROUPS = [
  'Search',
  'Details',
  'Organization',
  'Routing',
  'Date Created',
  'Classification',
  'Approval',
  'Additional Properties',
] as const

export type FilterGroup = (typeof FILTER_GROUPS)[number]

export interface FilterFieldMeta {
  key: FilterFieldKey
  label: string
  group: FilterGroup
  /** Key of the field this one narrows — hiding the parent leaves it unusable. */
  dependsOn?: FilterFieldKey
  /** Short note shown under the label in the field picker. */
  hint?: string
}

export const FILTER_FIELDS: FilterFieldMeta[] = [
  { key: 'keyword', label: 'Keyword search', group: 'Search', hint: 'Subject, name, handle or tag' },

  { key: 'referenceId', label: 'Conversation ID', group: 'Details', hint: 'e.g. TKT-2026-000007' },
  { key: 'subject', label: 'Subject', group: 'Details', hint: 'Case sensitive' },
  { key: 'description', label: 'Description', group: 'Details', hint: 'Case sensitive' },

  { key: 'clientId', label: 'Client', group: 'Organization' },
  { key: 'locationId', label: 'Location', group: 'Organization' },
  { key: 'branchId', label: 'Branch', group: 'Organization', dependsOn: 'locationId' },
  { key: 'departmentId', label: 'Department', group: 'Organization', dependsOn: 'branchId' },

  { key: 'campaignId', label: 'Campaign', group: 'Routing' },
  { key: 'teamId', label: 'Team', group: 'Routing', dependsOn: 'campaignId' },
  { key: 'agents', label: 'Agents', group: 'Routing', hint: 'Dual-list agent picker' },

  { key: 'dateRange', label: 'Created from / upto', group: 'Date Created' },

  { key: 'channel', label: 'Channel/Source', group: 'Classification' },
  { key: 'typeId', label: 'Type', group: 'Classification' },
  { key: 'conversationStatusId', label: 'Status', group: 'Classification' },
  { key: 'status', label: 'Lifecycle', group: 'Classification', hint: 'Open / on hold / reopened / closed' },
  { key: 'stateId', label: 'State', group: 'Classification' },
  { key: 'categoryId', label: 'Category', group: 'Classification' },
  { key: 'subcategoryId', label: 'Sub Category', group: 'Classification', dependsOn: 'categoryId' },
  { key: 'impactId', label: 'Impact', group: 'Classification' },
  { key: 'urgencyId', label: 'Urgency', group: 'Classification' },
  { key: 'priorityId', label: 'Priority', group: 'Classification' },
  { key: 'complexityId', label: 'Complexity', group: 'Classification' },

  { key: 'approvalStateId', label: 'Approval State', group: 'Approval' },
  { key: 'approvalLevelId', label: 'Approval Level', group: 'Approval' },

  { key: 'dynamicProperty', label: 'Additional Property', group: 'Additional Properties', hint: 'Property + value pair' },
]

/**
 * Shown to a user who has not customised the form: the conversation lookup fields
 * plus the campaign → team → agents routing chain.
 */
export const DEFAULT_FILTER_FIELDS: FilterFieldKey[] = [
  'referenceId',
  'subject',
  'description',
  'campaignId',
  'teamId',
  'agents',
]

const VALID_KEYS = new Set<string>(FILTER_FIELDS.map((f) => f.key))

/**
 * Coerce a stored/persisted selection to known keys, de-duplicated and ordered
 * as the panel renders them. An empty (or unusable) selection falls back to the
 * defaults so the panel is never blank.
 */
export function sanitizeFilterFields(saved: unknown): FilterFieldKey[] {
  if (!Array.isArray(saved)) return DEFAULT_FILTER_FIELDS
  const picked = new Set<string>()
  for (const v of saved) {
    if (typeof v === 'string' && VALID_KEYS.has(v)) picked.add(v)
  }
  if (picked.size === 0) return DEFAULT_FILTER_FIELDS
  return FILTER_FIELDS.filter((f) => picked.has(f.key)).map((f) => f.key)
}
